import type {
  Channel,
  WhatsappConfig,
  WhatsappCoexistenceSyncState,
} from "@/types/channel";
import type { WhatsappTemplate } from "@/types/whatsapp/template";

import { generateClinicWhatsappTemplates } from "../generators/whatsapp-template";
import { useMockStore } from "../store";

/**
 * Sync WhatsApp profile
 */
export function syncWhatsappProfileMock(channelId: string): Channel {
  const store = useMockStore.getState();
  const channel = store.channels.find((c) => c.id === channelId);

  if (!channel) {
    throw new Error("Channel not found");
  }

  return channel;
}

/**
 * Update WhatsApp profile
 */
export function updateWhatsappProfileMock(
  channelId: string,
  profile: any,
): Channel {
  const store = useMockStore.getState();
  const channel = store.channels.find((c) => c.id === channelId);

  if (!channel) {
    throw new Error("Channel not found");
  }

  const updatedChannel = {
    ...channel,
    config: {
      ...channel.config,
      profile,
    },
  };

  store.updateChannel(channelId, updatedChannel);

  return updatedChannel;
}

/**
 * Sync phone number
 */
export function syncPhoneNumberMock(channelId: string): Channel {
  const store = useMockStore.getState();
  const channel = store.channels.find((c) => c.id === channelId);

  if (!channel) {
    throw new Error("Channel not found");
  }

  return channel;
}

/**
 * Update coexistence sync status
 */
export function updateCoexistenceSyncStatusMock(
  channelId: string,
  state: WhatsappCoexistenceSyncState,
): Channel {
  const store = useMockStore.getState();
  const channel = store.channels.find((c) => c.id === channelId);

  if (!channel) {
    throw new Error("Channel not found");
  }

  const config = channel.config as WhatsappConfig;

  const updatedChannel: Channel = {
    ...channel,
    config: {
      ...config,
      coexistenceSync: { ...config.coexistenceSync, state },
    },
  };

  store.updateChannel(channelId, updatedChannel);

  return updatedChannel;
}

/** The channel's place among the workspace's WhatsApp numbers: each has its own templates. */
function numberIndexOf(channelId: string): number {
  const index = useMockStore
    .getState()
    .channels.filter((channel) => channel.type === "whatsapp")
    .findIndex((channel) => channel.id === channelId);

  return Math.max(index, 0);
}

/**
 * Get WhatsApp templates
 */
export function getWhatsappTemplatesMock(
  channelId: string,
): WhatsappTemplate[] {
  return generateClinicWhatsappTemplates(channelId, numberIndexOf(channelId));
}

/**
 * Get a single WhatsApp template
 */
export function getWhatsappTemplateMock(
  channelId: string,
  templateId: string,
): WhatsappTemplate {
  const templates = getWhatsappTemplatesMock(channelId);

  return templates.find((t) => t.id === templateId) ?? templates[0];
}

/** Meta only sends an approved template; the server answers 422 otherwise. */
export function sendWhatsappTemplateTestMock(
  channelId: string,
  templateId: string,
): { sent: boolean } {
  const template = getWhatsappTemplatesMock(channelId).find(
    (t) => t.id === templateId,
  );

  if (template?.status !== "approved") {
    throw new Error("Only an approved template can be sent as a test.");
  }

  return { sent: true };
}

/**
 * Create WhatsApp template
 */
export function createWhatsappTemplateMock(
  channelId: string,
  payload: any,
): WhatsappTemplate {
  return {
    id: `template-${Date.now()}`,
    name: payload.name || "new_template",
    externalId: `mock-external-${Date.now()}`,
    channelId,
    organizationId: "mock-org-id",
    workspaceId: "mock-workspace-id",
    parameterFormat: payload.parameterFormat || "positional",
    language: payload.language || "en",
    status: "pending",
    qualityScore: "unknown",
    qualityScoreDate: null,
    category: payload.category || "marketing",
    components: payload.components || [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    lastEditedAt: null,
    createdBy: "mock-user-id",
    lastEditedBy: "mock-user-id",
    deletedAt: null,
    lastUsedAt: null,
  };
}

/**
 * Update WhatsApp template
 */
export function updateWhatsappTemplateMock(
  channelId: string,
  templateId: string,
  payload: any,
): WhatsappTemplate {
  return {
    id: templateId,
    name: payload.name || "updated_template",
    externalId: "mock-external-id",
    channelId,
    organizationId: "mock-org-id",
    workspaceId: "mock-workspace-id",
    parameterFormat: payload.parameterFormat || "positional",
    language: payload.language || "en",
    status: "pending",
    qualityScore: "unknown",
    qualityScoreDate: null,
    category: payload.category || "marketing",
    components: payload.components || [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    lastEditedAt: new Date().toISOString(),
    createdBy: "mock-user-id",
    lastEditedBy: "mock-user-id",
    deletedAt: null,
    lastUsedAt: null,
  };
}

/**
 * Delete WhatsApp template
 */
export function deleteWhatsappTemplateMock(): boolean {
  return true;
}

/**
 * Sync WhatsApp templates
 */
export function syncWhatsappTemplatesMock(): { success: boolean } {
  return { success: true };
}
